import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { claimPrem90 } from '@/shared/api/user/wallet/claim-prem-90.api';
import { claimPrem365 } from '@/shared/api/user/wallet/claim-prem-365.api';
import { withdraw } from '@/shared/api/user/wallet/withdraw.api';

import {
  type Wallet,
  type WalletState,
  type WalletStore,
  WalletType,
} from './wallet.types';

const defaultWalletState: WalletState = {
  wallets: undefined,
  pauseWalletWS: false,
};

export const useWalletStateRaw = create<WalletStore>()((set, get) => ({
  ...defaultWalletState,
  actions: {
    hydrate: (fresh) => set(fresh),
    grabPrem90: async () => {
      try {
        await claimPrem90();
      } catch (err) {
        console.error('ERROR in grab prem 90', err);
      }
    },
    grabPrem365: async () => {
      try {
        await claimPrem365();
      } catch (err) {
        console.error('ERROR in grab prem 365', err);
      }
    },
    // use for TON or NOT only
    withdrawPrize: async (prize: WalletType) => {
      try {
        await withdraw(prize);
      } catch (err) {
        console.error('ERROR in withdrawPrize', err);
      }
    },

    hydrateWallet: (walletType, wallet) => {
      set((prev) => {
        const wallets =
          prev?.wallets?.map((prevWallet) => {
            if (prevWallet.type === walletType) {
              return typeof wallet === 'function' ? wallet(prevWallet) : wallet;
            }

            return prevWallet;
          }) ?? [];

        return { wallets };
      });
    },

    togglePauseWalletWS: (pauseWalletWS) => {
      set({ pauseWalletWS });
    },

    getWalletBalances: () => {
      const wallets = get().wallets;

      const walletBalances: Record<WalletType, number> = {
        [WalletType.PLAYCOIN]: 0,
        [WalletType.PREMIUM]: 0,
        [WalletType.PREMIUM_365]: 0,
        [WalletType.TICKET]: 0,
        [WalletType.NOT]: 0,
        [WalletType.TON]: 0,
        [WalletType.XP]: 0,
      };

      if (!wallets?.length) return walletBalances;

      for (const wallet of wallets) {
        walletBalances[wallet.type] = wallet.balance;
      }

      return walletBalances;
    },
  },
}));

export const useWalletState = () =>
  useWalletStateRaw(
    useShallow((state) => ({
      wallets: state.wallets,
      pauseWalletWS: state.pauseWalletWS,
    }))
  );

export const useWalletActions = () => useWalletStateRaw((state) => state.actions);

export const useWallets = () => {
  return useWalletStateRaw(useShallow((state) => state.wallets));
};

export const usePauseWalletWS = () => {
  return useWalletStateRaw(useShallow((state) => state.pauseWalletWS));
};

const useWallet = (walletType: WalletType) => {
  return useWalletStateRaw(
    useShallow((state) => state?.wallets?.find((w) => w.type === walletType))
  );
};

const makeUseWallet = (walletType: WalletType) => () => {
  return useWallet(walletType) ?? ({} as Wallet);
};

export const useWalletBalances = () =>
  useWalletStateRaw(
    useShallow((state) => {
      const wallets = state.wallets;

      const walletBalances: Record<WalletType, number> = {
        [WalletType.PLAYCOIN]: 0,
        [WalletType.PREMIUM]: 0,
        [WalletType.PREMIUM_365]: 0,
        [WalletType.TICKET]: 0,
        [WalletType.NOT]: 0,
        [WalletType.TON]: 0,
        [WalletType.XP]: 0,
      };

      if (!wallets?.length) return walletBalances;

      for (const wallet of wallets) {
        walletBalances[wallet.type] = wallet.balance;
      }

      return walletBalances;
    })
  );

export const usePlaycoinWallet = makeUseWallet(WalletType.PLAYCOIN);
export const useTicketWallet = makeUseWallet(WalletType.TICKET);
export const useXPWallet = makeUseWallet(WalletType.XP);
export const usePremium365Wallet = makeUseWallet(WalletType.PREMIUM_365);
export const usePremiumWallet = makeUseWallet(WalletType.PREMIUM);
export const useTonWallet = makeUseWallet(WalletType.TON);
export const useNotWallet = makeUseWallet(WalletType.NOT);
